import React, { useEffect, useState } from "react";
import { X, Check, Loader2, KeyRound, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function RiddleEditor({ open, onClose }) {
  const [riddles, setRiddles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [resetting, setResetting] = useState(false);

  const loadRiddles = (data) => {
    const sorted = [...data].sort((a, b) => a.day - b.day);
    setRiddles(sorted.map((r) => ({ ...r, answersText: (r.answers || []).join(", ") })));
  };

  const resetDefaults = async () => {
    if (!window.confirm("Restore the original cute riddles? This will replace your current 7 riddles.")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/gate/riddles/reset");
      loadRiddles(data);
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/gate/riddles")
      .then(({ data }) => loadRiddles(data))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const update = (id, field, value) =>
    setRiddles((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const saveOne = async (r) => {
    setSavingId(r.id);
    const answers = r.answersText.split(",").map((a) => a.trim()).filter(Boolean);
    try {
      await api.patch(`/gate/riddles/${r.id}`, {
        question: r.question,
        hint: r.hint,
        answers,
      });
      setSavedId(r.id);
      setTimeout(() => setSavedId(null), 1600);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="riddle-editor"
    >
      <div
        className="card-paper rounded-3xl p-7 w-full max-w-2xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#D95F59]" />
            <h3 className="font-display text-2xl">Edit your daily riddles</h3>
          </div>
          <button data-testid="close-riddle-editor" onClick={onClose} className="text-[#756D6A] hover:text-[#2A2726]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-[#756D6A] mb-4">
          One riddle per day of the week. Separate multiple accepted answers with commas.
        </p>
        <button
          data-testid="reset-riddles-btn"
          onClick={resetDefaults}
          disabled={resetting}
          className="flex items-center gap-1.5 rounded-full border border-[#EADEDA] text-[#756D6A] px-4 py-1.5 text-sm hover:bg-[#F4F1EA] transition-colors mb-5 disabled:opacity-50"
        >
          {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          Reset to original riddles
        </button>

        {loading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-7 h-7 text-[#D95F59] animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            {riddles.map((r) => (
              <div key={r.id} className="rounded-2xl border border-[#EADEDA] bg-[#FFF5F5] p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-display text-lg text-[#D95F59]">{DAYS[r.day]}</span>
                  <button
                    data-testid={`save-riddle-${r.day}`}
                    onClick={() => saveOne(r)}
                    disabled={savingId === r.id}
                    className="flex items-center gap-1.5 rounded-full bg-[#D95F59] text-white px-4 py-1.5 text-sm hover:bg-[#C75D53] disabled:opacity-50"
                  >
                    {savingId === r.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : savedId === r.id ? (
                      <Check className="w-4 h-4" />
                    ) : null}
                    {savedId === r.id ? "Saved" : "Save"}
                  </button>
                </div>
                <textarea
                  data-testid={`riddle-question-${r.day}`}
                  value={r.question}
                  onChange={(e) => update(r.id, "question", e.target.value)}
                  rows={2}
                  placeholder="The riddle / question"
                  className="w-full rounded-xl border border-[#EADEDA] px-3 py-2 bg-white outline-none focus:border-[#D95F59] resize-none mb-2"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    data-testid={`riddle-answers-${r.day}`}
                    value={r.answersText}
                    onChange={(e) => update(r.id, "answersText", e.target.value)}
                    placeholder="Answers (comma separated)"
                    className="w-full rounded-full border border-[#EADEDA] px-3 py-2 bg-white outline-none focus:border-[#D95F59] text-sm"
                  />
                  <input
                    data-testid={`riddle-hint-${r.day}`}
                    value={r.hint}
                    onChange={(e) => update(r.id, "hint", e.target.value)}
                    placeholder="Hint (optional)"
                    className="w-full rounded-full border border-[#EADEDA] px-3 py-2 bg-white outline-none focus:border-[#D95F59] text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
